/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {AxiosError, AxiosInstance} from 'axios';
import {ServiceError} from '../types/services';

/**
 * @errorInterceptor
 *
 * The errorInterceptor provides a utility which allows for adding a global
 * error interceptor to an axios client instance, accepting an error function
 * which will be invoked whenever an exception occurs.
 *
 * The error function will be invoked with an augmented error object which
 * contains additional information about the error, such as the error status,
 * if the error is critical, a warning, a server error, an unauthorized error,
 * a permissions error, a not found error, if the user is offline, etc..
 *
 * The error object returned will contain the following properties:
 *
 *  - status              : The HTTP Status Code (401, 403, 404, 500, etc.)
 *  - errorCode           : The HTTP Status Code (convenience alias for status),
 *  - method              : The HTTP Request method (get, post, put, delete),
 *  - url                 : The URL of the failed request
 *  - isCritical          : True if the error is deemed critical, otherwise false.
 *  - isWarning           : True if the error is deemed a warning, otherwise false.
 *  - is401               : True if the error is a 401 (unauthorized) error.
 *  - is403               : True if the error is a 403 (permissions) error.
 *  - is404               : True if the error is a 404 (not found) error.
 *  - is429               : True if the error is a 429 (rate limited) error.
 *  - is500               : True if the error is a 500 (server) error.
 *  - isUnauthorizedError : Convenience alias for is401.
 *  - isPermissionsError  : Convenience alias for is403.
 *  - isNotFoundError     : Convenience alias for is404.
 *  - isRateLimitError    : Convenience alias for is429.
 *  - isServerError       : Convenience alias for is500.
 *  - isUnresolvableError : True if the error status can not be resolved, this can
 *                          occur if a CORS request fails during options preflight.
 *  - isOffline           : True if the user's network connection is offline.
 *  - error               : The original Axios Error object which contains
 *                          additional information
 *
 * ```typescript
 *
 *   import {errorInterceptor} from '@ion/core/src/services';
 *   import {ServiceError} from '@ion/core/src/types/services';
 *
 *   // add a global error interceptor, returning a promise rejection so as to
 *   // allow additional error resolving on a case by case basis ...
 *   export const addErrorInterceptor = dispatch => {
 *     const interceptor = (error: ServiceError) => {
 *       dispatch(serviceError(error));
 *       logger(error.response);
 *       return Promise.reject(error);
 *     };
 *     setGlobalErrorHandler(interceptor);
 *    };
 *
 * ```
 */
export const errorInterceptor = (client: AxiosInstance, onError: (e: ServiceError) => void) => {
  client?.interceptors?.response?.use(
    response => response,
    error => onError(errorResolver(error))
  );
};

/**
 * @errorNormalizer
 *
 * The errorNormalizer provides a utility which normalizes an axios error by
 * adding additional properties of interest to the top-level error object, such
 * as the type of error, etc.
 */
export const errorResolver = (error: AxiosError): ServiceError => {
  const status = error.response?.status || 0;
  const is401 = isUnauthorizedError(status);
  const is403 = isPermissionsError(status);
  const is404 = isNotFoundError(status);
  const is429 = isRateLimitError(status);
  const is500 = isServerError(status);
  return {
    errorCode           : status,
    message             : error.message,
    method              : error.config?.method,
    data                : error.config?.data,
    response            : error.response,
    url                 : error.config?.url,
    errorText           : error.response?.data?.response?.errors[0],
    isOffline           : isOffline(),
    isCritical          : isCritical(status),
    isWarning           : isWarning(status),
    isUnresolvableError : isUnresolvableError(status),
    isUnauthorizedError : is401,
    isPermissionsError  : is403,
    isNotFoundError     : is404,
    isRateLimitError    : is429,
    isServerError       : is500,
    status,
    error,
    is401,
    is403,
    is404,
    is429,
    is500
  } as ServiceError;
};

/**
 * @isCritical
 *
 * Determines if the given error should be considered critical (500 errors).
 */
export const isCritical = (status: number): boolean => !status || status >= 500;

/**
 * @isWarning
 *
 * Determines if the given error should be considered a warning (400 errors).
 */
export const isWarning = (status: number): boolean => status >= 400 && status < 500;

/**
 * @isServerError
 *
 * Determines if the given error is a server error (500, 502, 503, etc.).
 */
export const isServerError = (status: number): boolean => status >= 500;

/**
 * @isUnauthorizedError
 *
 * Determines if the given error is an unauthorized (401) error.
 */
export const isUnauthorizedError = (status: number): boolean => status === 401;

/**
 * @isPermissionsError
 *
 * Determines if the given error is a permissions (403) error.
 */
export const isPermissionsError = (status: number): boolean => status === 403;

/**
 * @isNotFoundError
 *
 * Determines if the given error is an not found (404) error.
 */
export const isNotFoundError = (status: number): boolean => status === 404;

/**
 * @isRateLimitError
 *
 * Determines if the given error is a rate limited (429) error, returned when a
 * caller has exhausted the attempts an upstream service permits within its
 * cool-off window.
 */
export const isRateLimitError = (status: number): boolean => status === 429;

/**
 * @isUnresolvableError
 *
 * Determines if the given error status can not be resolved.
 */
export const isUnresolvableError = (status?: number): boolean => !status;

/**
 * @isOffline
 *
 * Determines if the user is currently offline ...
 */
export const isOffline = (global?: Window | any) => !(global || window)?.navigator?.onLine;

/**
 * @isSuccess
 *
 * Determines if the given status is a success (200 || 201) status.
 */
export const isSuccess = (status: number): boolean => [200, 201, 204].indexOf(status) > -1;
